<template>
  <div class="relative">
    <Table>
      <TableHeader class="bg-grey-light-50 whitespace-nowrap font-medium">
        <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
          <TableHead v-for="header in headerGroup.headers" :key="header.id">
            <FlexRender
              v-if="!header.isPlaceholder"
              :render="header.column.columnDef.header"
              :props="header.getContext()"
            />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="isLoading">
          <TableRow v-for="skeletonRow in skeletonRows" :key="skeletonRow">
            <TableCell v-for="(column, index) in columns" :key="index">
              <Skeleton class="h-[23px] w-full" />
            </TableCell>
          </TableRow>
        </template>
        <template v-else-if="table.getRowModel().rows?.length">
          <TableRow
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :data-state="row.getIsSelected() ? 'selected' : undefined"
          >
            <TableCell v-for="cell in row.getVisibleCells()" :key="cell.id">
              <div class="min-h-[23px]">
                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </div>
            </TableCell>
          </TableRow>
        </template>
        <template v-else>
          <TableRow>
            <TableCell :colspan="columns.length" class="h-24 text-center"> No results. </TableCell>
          </TableRow>
        </template>
      </TableBody>
    </Table>

    <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white/50">
      <LayupSpinner :size="8" />
    </div>
  </div>

  <div v-if="showPagination" class="my-4 px-4 grid grid-cols-3">
    <div class="col-span-1">
      <div class="flex items-center space-x-2">
        <p class="text-sm font-medium text-grey-light-600">Rows per page</p>
        <Select :model-value="`${generalStore.pageSize}`" @update:model-value="onUpdatePageSize(Number($event))">
          <SelectTrigger class="h-8 w-[70px]">
            <SelectValue :placeholder="`${generalStore.pageSize}`" />
          </SelectTrigger>
          <SelectContent side="top">
            <SelectItem
              v-for="availablePageSizes in generalStore.getPageSizeOptions()"
              :key="availablePageSizes"
              :value="`${availablePageSizes}`"
            >
              {{ availablePageSizes }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div class="col-span-1 flex justify-center">
      <Pagination
        v-slot="{ page }"
        :total="totalCount"
        :itemsPerPage="generalStore.pageSize"
        :page="pageNumber"
        :default-page="1"
      >
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
          <PaginationFirst @click="onUpdatePage(1)" />
          <PaginationPrev @click="onUpdatePage(pageNumber - 1)" />

          <template v-for="(item, index) in items">
            <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button
                class="w-10 h-10 p-0"
                @click="onUpdatePage(item.value)"
                :variant="item.value === page ? 'primary' : 'outline'"
              >
                {{ item.value }}
              </Button>
            </PaginationListItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>

          <PaginationNext @click="onUpdatePage((pageNumber ?? 0) + 1)" />
          <PaginationLast @click="onUpdatePage(totalPages)" />
        </PaginationList>
      </Pagination>
    </div>

    <div class="col-span-1 flex justify-end">
      <div class="flex items-center justify-center text-sm font-medium text-grey-light-600">
        Page {{ pageNumber }} of
        {{ totalPages }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TData, TValue">
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { valueUpdater } from '@/lib/utils';
import { useGeneralStore } from '@/stores/general';
import type { ColumnDef, SortingState, VisibilityState } from '@tanstack/vue-table';
import { FlexRender, getCoreRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table';
import { computed, ref } from 'vue';
import LayupSpinner from '../common/LayupSpinner.vue';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

const props = withDefaults(
  defineProps<{
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    isLoading?: boolean;
    totalCount?: number;
    pageNumber?: number;
    showPagination?: boolean;
    manualSorting?: boolean; // true = server-side, false = client-side
  }>(),
  {
    isLoading: false,
    totalCount: 0,
    pageNumber: 1,
    showPagination: true,
    manualSorting: true,
  },
);

const generalStore = useGeneralStore();
const sorting = ref<SortingState>([]);
const columnVisibility = ref<VisibilityState>({});

const emit = defineEmits(['update:page', 'update:pageSize', 'update:sorting']);

const onSortingChange = (updaterOrValue: any) => {
  valueUpdater(updaterOrValue, sorting);
  if (props.manualSorting) {
    emit('update:sorting', sorting.value);
  }
  // If manualSorting is false, sorting is handled locally (client-side)
};

const table = useVueTable({
  get data() {
    return props.data;
  },
  get columns() {
    return props.columns;
  },
  getCoreRowModel: getCoreRowModel(),
  manualSorting: props.manualSorting,
  getSortedRowModel: getSortedRowModel(),
  onSortingChange,
  onColumnVisibilityChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnVisibility),
  state: {
    get sorting() {
      return sorting.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
  },
});

const onUpdatePage = (page: number) => {
  emit('update:page', page);
};

const onUpdatePageSize = (size: number) => {
  emit('update:pageSize', size);
};

const skeletonRows = computed(() => {
  return Array.from({ length: generalStore.pageSize }, (_, i) => i + 1);
});

const totalPages = computed(() => {
  return Math.ceil(props.totalCount / generalStore.pageSize);
});
</script>
